import { useState, useEffect, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../services/api';
import { formatRelative } from '../utils/dates';
import CompetitorInsights from '../components/CompetitorInsights';
import { trackEvent } from '../services/analytics';
import GetStartedCard from '../components/GetStartedCard';
import StandingCard from '../components/StandingCard';

const PERIOD_OPTIONS = [
  { value: '1m', label: '1M' },
  { value: '3m', label: '3M' },
  { value: '6m', label: '6M' },
  { value: '12m', label: '12M' },
  { value: 'all', label: 'All' },
];

const RATING_COLORS = {
  1: '#d44343',
  2: '#e8734a',
  3: '#d4a843',
  4: '#6bb87a',
  5: '#3a9e6e',
};

const RATING_LABELS = {
  5: 'Excellent',
  4: 'Good',
  3: 'Average',
  2: 'Poor',
  1: 'Terrible',
};

function cleanStatus(status) {
  if (!status) return '';
  const str = String(status);
  if (str.includes('.')) return str.split('.').pop().toLowerCase();
  return str.toLowerCase();
}

/* ─── Animated card wrapper ─── */
function FadeInCard({ children, delay = 0, style = {}, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      ref={ref}
      className={`card ${className}`}
      style={{
        ...style,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(14px)',
        transition: 'opacity 0.45s ease, transform 0.45s ease',
      }}
    >
      {children}
    </div>
  );
}

/* ─── Skeleton placeholders ─── */
function SkeletonBlock({ width = '100%', height = 16, radius = 6, style = {} }) {
  return (
    <div
      className="skeleton-shimmer"
      style={{
        width,
        height,
        borderRadius: radius,
        ...style,
      }}
    />
  );
}

function SkeletonStatCard() {
  return (
    <div className="card" style={{ padding: 20 }}>
      <SkeletonBlock width={80} height={12} style={{ marginBottom: 12 }} />
      <SkeletonBlock width={60} height={32} radius={8} />
    </div>
  );
}

function SkeletonChartCard() {
  return (
    <div className="card" style={{ padding: 24 }}>
      <SkeletonBlock width={140} height={12} style={{ marginBottom: 20 }} />
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 180 }}>
        {[60, 90, 45, 110, 70, 85, 55].map((h, i) => (
          <SkeletonBlock key={i} width="100%" height={h} radius={4} />
        ))}
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div>
      {/* Period tabs skeleton */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[1, 2, 3, 4, 5].map(i => (
          <SkeletonBlock key={i} width={48} height={32} radius={6} />
        ))}
      </div>

      {/* Stats row */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <SkeletonStatCard />
        <SkeletonStatCard />
        <SkeletonStatCard />
        <SkeletonStatCard />
      </div>

      {/* Highlights skeleton */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <SkeletonBlock width={200} height={14} style={{ marginBottom: 12 }} />
        <SkeletonBlock width="90%" height={12} style={{ marginBottom: 8 }} />
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {[80, 100, 70, 90].map((w, i) => (
            <SkeletonBlock key={i} width={w} height={28} radius={20} />
          ))}
        </div>
      </div>

      {/* Charts row */}
      <div className="dashboard-charts-grid" style={{ marginBottom: 24 }}>
        <SkeletonChartCard />
        <SkeletonChartCard />
      </div>
    </div>
  );
}

/* ─── Rating Distribution Bar ─── */
function RatingDistribution({ distribution, total }) {
  if (!distribution || total === 0) return null;

  const ratings = [5, 4, 3, 2, 1];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {ratings.map(r => {
        const count = distribution[r] || distribution[String(r)] || 0;
        const pct = total > 0 ? (count / total) * 100 : 0;

        return (
          <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontSize: 12, fontWeight: 600, color: 'var(--text-light)',
              width: 16, textAlign: 'right', flexShrink: 0,
            }}>
              {r}★
            </span>
            <div style={{
              flex: 1,
              height: 10,
              background: 'var(--bg)',
              borderRadius: 5,
              overflow: 'hidden',
            }}>
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: RATING_COLORS[r],
                  borderRadius: 5,
                  transition: 'width 0.6s ease',
                  minWidth: count > 0 ? 4 : 0,
                }}
              />
            </div>
            <span style={{
              fontSize: 11, color: 'var(--text-light)',
              width: 28, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums',
            }}>
              {count}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Highlights section with cap ─── */
function HighlightPills({ items, color, bgAlpha, borderAlpha, icon, label, maxVisible = 5 }) {
  const [expanded, setExpanded] = useState(false);

  if (!items || items.length === 0) return null;

  const visible = expanded ? items : items.slice(0, maxVisible);
  const remaining = items.length - maxVisible;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{
          width: 24, height: 24, borderRadius: '50%',
          background: bgAlpha,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13,
        }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingLeft: 32 }}>
        {visible.map((s, i) => (
          <span key={i} style={{
            padding: '5px 12px',
            background: bgAlpha,
            border: `1px solid ${borderAlpha}`,
            borderRadius: 20,
            fontSize: 13,
            fontWeight: 500,
            color: color,
          }}>
            {s}
          </span>
        ))}
        {!expanded && remaining > 0 && (
          <button
            onClick={() => setExpanded(true)}
            style={{
              padding: '5px 12px',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text-light)',
              cursor: 'pointer',
            }}
          >
            +{remaining} more
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Dashboard styles (injected once) ─── */
function DashboardStyles() {
  return (
    <style>{`
      .dashboard-charts-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        margin-bottom: 24px;
      }
      .dashboard-stats-detail {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        margin-bottom: 24px;
      }
      @media (max-width: 768px) {
        .dashboard-charts-grid {
          grid-template-columns: 1fr;
        }
        .dashboard-stats-detail {
          grid-template-columns: 1fr;
        }
        .stats-grid {
          grid-template-columns: 1fr 1fr !important;
        }
        .filter-tabs {
          gap: 4px !important;
        }
        .filter-tab {
          padding: 6px 10px !important;
          font-size: 12px !important;
        }
      }
      @media (max-width: 480px) {
        .stats-grid {
          grid-template-columns: 1fr !important;
        }
      }

      /* Skeleton shimmer */
      .skeleton-shimmer {
        background: linear-gradient(
          90deg,
          var(--bg) 25%,
          rgba(255,255,255,0.06) 50%,
          var(--bg) 75%
        );
        background-size: 200% 100%;
        animation: shimmer 1.5s ease-in-out infinite;
      }
      @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }

      /* Stat delta styles */
      .stat-delta {
        font-size: 11px;
        font-weight: 600;
        margin-top: 4px;
        display: inline-flex;
        align-items: center;
        gap: 2px;
        padding: 2px 8px;
        border-radius: 10px;
      }
      .stat-delta.up {
        color: #2ab380;
        background: rgba(52, 211, 153, 0.1);
      }
      .stat-delta.down {
        color: #d44343;
        background: rgba(212, 67, 67, 0.1);
      }
      .stat-delta.neutral {
        color: var(--text-light);
        background: var(--bg);
      }

      /* Review link banner */
      .review-link-banner {
        border-left: 3px solid var(--accent, #34d399);
      }

      /* Tooltip override for recharts */
      .recharts-tooltip-wrapper {
        outline: none;
      }

      /* Pulse animation for highlights loading */
      @keyframes pulse {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }
    `}</style>
  );
}

/* ─── Main Dashboard ─── */
export default function DashboardPage() {
  const [analytics, setAnalytics] = useState(null);
  const [business, setBusiness] = useState(null);
  const [period, setPeriod] = useState('6m');
  const [loading, setLoading] = useState(true);
  const [reviewLink, setReviewLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [highlights, setHighlights] = useState(null);
  const [highlightsLoading, setHighlightsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const businesses = await api.getBusinesses();
        if (businesses.length > 0) setBusiness(businesses[0]);

        const data = await api.get('/analytics/dashboard?period=' + period);
        setAnalytics(data);
        trackEvent('dashboard_visit', { period });
      } catch (err) {
        console.error('Failed to load analytics:', err);
        try {
          const reviews = await api.getReviews();
          const total = reviews.length;
          const responded = reviews.filter(r =>
            ['approved', 'posted', 'generated'].includes(cleanStatus(r.status))
          ).length;
          const avgRating = total > 0
            ? (reviews.reduce((sum, r) => sum + r.rating, 0) / total).toFixed(1)
            : 0;
          const pending = reviews.filter(r =>
            ['pending', 'generated'].includes(cleanStatus(r.status))
          ).length;
          setAnalytics({
            response_metrics: {
              total, pending,
              response_rate: total > 0 ? Math.round((responded / total) * 100) : 0,
            },
            rating_trend: { current_avg: parseFloat(avgRating), direction: 'neutral' },
            rating_distribution: {},
            reviews_over_time: [],
            source_breakdown: [],
            individual_reviews: [],
            recent_reviews: reviews.slice(-5).reverse().map(r => ({
              id: r.id,
              rating: r.rating,
              reviewer_name: r.reviewer_name,
              date: r.review_created_at || r.created_at,
              status: r.status,
            })),
          });
        } catch {
          setAnalytics(null);
        }
      }

      try {
        const link = await api.get('/review-link');
        setReviewLink(link);
      } catch { /* no link set up yet */ }

      setLoading(false);
    };
    fetchData();
  }, [period]);

  useEffect(() => {
    const fetchHighlights = async () => {
      setHighlightsLoading(true);
      try {
        const data = await api.get('/analytics/highlights');
        setHighlights(data);
      } catch {
        setHighlights(null);
      }
      setHighlightsLoading(false);
    };
    fetchHighlights();
  }, []);

  const copyLink = () => {
    if (reviewLink?.full_link) {
      navigator.clipboard.writeText(reviewLink.full_link);
      trackEvent('review_link_copied');
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div>
        <DashboardStyles />
        <div className="page-header">
          <div>
            <SkeletonBlock width={200} height={24} radius={8} style={{ marginBottom: 8 }} />
            <SkeletonBlock width={260} height={14} />
          </div>
        </div>
        <DashboardSkeleton />
      </div>
    );
  }

  if (!analytics) return <div className="loading">No data available</div>;

  const { reviews_over_time, response_metrics, source_breakdown, rating_trend, rating_distribution } = analytics;

  const trendDirection = rating_trend?.direction === 'up' ? '↑' : rating_trend?.direction === 'down' ? '↓' : '→';
  const trendColor = rating_trend?.direction === 'up' ? '#2ab380' : rating_trend?.direction === 'down' ? '#d44343' : 'var(--text-light)';

  return (
    <div>
      <DashboardStyles />

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{business ? business.name : 'Dashboard'}</h1>
          <p className="page-subtitle">Review analytics and performance</p>
        </div>
      </div>

      <StandingCard />

      <GetStartedCard />

      {/* Period Selector */}
      <div className="filter-tabs" style={{ marginBottom: 24 }}>
        {PERIOD_OPTIONS.map(opt => (
          <button
            key={opt.value}
            className={`filter-tab ${period === opt.value ? 'active' : ''}`}
            onClick={() => setPeriod(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Stats Row */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <FadeInCard delay={0} style={{ padding: 20 }}>
          <div className="stat-label">Total Reviews</div>
          <div className="stat-value">{response_metrics.total}</div>
          {response_metrics.total_delta != null && (
            <div className={`stat-delta ${response_metrics.total_delta > 0 ? 'up' : response_metrics.total_delta < 0 ? 'down' : 'neutral'}`}>
              {response_metrics.total_delta > 0 ? '+' : ''}{response_metrics.total_delta} vs prev
            </div>
          )}
        </FadeInCard>

        <FadeInCard delay={60} style={{ padding: 20 }}>
          <div className="stat-label">Avg Rating</div>
          <div className="stat-value accent">
            {rating_trend?.current_avg ? (
              <>
                {rating_trend.current_avg} ★
                <span style={{ fontSize: 16, marginLeft: 6, color: trendColor }}>{trendDirection}</span>
              </>
            ) : '—'}
          </div>
          {rating_trend?.previous_avg != null && (
            <div className={`stat-delta ${rating_trend.direction === 'up' ? 'up' : rating_trend.direction === 'down' ? 'down' : 'neutral'}`}>
              was {rating_trend.previous_avg} ★
            </div>
          )}
        </FadeInCard>

        <FadeInCard delay={120} style={{ padding: 20 }}>
          <div className="stat-label">Response Rate</div>
          <div className="stat-value green">{response_metrics.response_rate}%</div>
        </FadeInCard>

        <FadeInCard delay={180} style={{ padding: 20 }}>
          <div className="stat-label">Pending</div>
          <div className="stat-value blue">{response_metrics.pending}</div>
        </FadeInCard>
      </div>

      {/* Review Highlights Card */}
      <FadeInCard delay={220} style={{ padding: 24, marginBottom: 24 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          What Your Customers Are Saying
        </h3>
        <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 16 }}>
          AI-powered analysis of your recent reviews
        </p>
        {highlightsLoading ? (
          <div style={{ padding: '12px 0' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {[80, 100, 70, 90].map((w, i) => (
                <SkeletonBlock key={i} width={w} height={28} radius={20} />
              ))}
            </div>
            <SkeletonBlock width="80%" height={12} style={{ marginBottom: 8 }} />
            <SkeletonBlock width="60%" height={12} />
          </div>
        ) : highlights && (highlights.strengths?.length > 0 || highlights.improvements?.length > 0) ? (
          <div>
            {/* Summary first — it's the most valuable insight */}
            {highlights.summary && (
              <div style={{
                padding: '12px 16px',
                background: 'var(--bg)',
                borderRadius: 8,
                fontSize: 13,
                fontStyle: 'italic',
                color: 'var(--text-light)',
                lineHeight: 1.6,
                marginBottom: 16,
                borderLeft: '3px solid var(--accent, #34d399)',
              }}>
                {highlights.summary}
              </div>
            )}

            <HighlightPills
              items={highlights.strengths}
              color="#2ab380"
              bgAlpha="rgba(52,211,153,0.08)"
              borderAlpha="rgba(52,211,153,0.2)"
              icon="👍"
              label="Customers love"
            />

            <HighlightPills
              items={highlights.improvements}
              color="#b8941e"
              bgAlpha="rgba(212,168,67,0.08)"
              borderAlpha="rgba(212,168,67,0.2)"
              icon="💡"
              label="Could improve"
            />

            <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 12, opacity: 0.6 }}>
              Based on {highlights.review_count} review{highlights.review_count !== 1 ? 's' : ''} · Updates daily
            </div>
          </div>
        ) : highlights?.summary ? (
          <div style={{ padding: '16px 0', fontSize: 13, color: 'var(--text-light)' }}>
            {highlights.summary}
          </div>
        ) : (
          <div style={{ padding: '16px 0', fontSize: 13, color: 'var(--text-light)' }}>
            Add at least 3 reviews with text to unlock AI-powered insights about what your customers think.
          </div>
        )}
      </FadeInCard>

      {/* Charts Row */}
      <div className="dashboard-charts-grid">
        {/* Reviews Over Time */}
        <FadeInCard delay={280} style={{ padding: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Reviews Over Time
          </h3>
          {analytics.individual_reviews && analytics.individual_reviews.length > 0 ? (
            <div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                padding: '12px 0',
                minHeight: 180,
                alignItems: 'flex-end',
                alignContent: 'flex-end',
              }}>
                {analytics.individual_reviews.map((review) => (
                  <div
                    key={review.id}
                    title={`${review.reviewer_name}\n${review.rating} ★ — ${formatRelative(review.date)}`}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: RATING_COLORS[review.rating] || '#999',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'default',
                      transition: 'transform 0.15s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.15)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    {review.rating}
                  </div>
                ))}
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  {[5, 4, 3, 2, 1].map(r => (
                    <span key={r} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-light)' }}>
                      <span style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: RATING_COLORS[r], display: 'inline-block',
                      }} />
                      {r}★
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : reviews_over_time.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={reviews_over_time} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="period_label" tick={{ fontSize: 11, fill: 'var(--text-light)' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--text-light)' }} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                  formatter={(value, name) => {
                    if (name === 'count') return [value, 'Reviews'];
                    if (name === 'avg_rating') return [value, 'Avg Rating'];
                    return [value, name];
                  }}
                />
                <Bar dataKey="count" fill="#34d399" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state" style={{ padding: 24 }}>
              <div className="empty-state-text">No review data yet</div>
            </div>
          )}
        </FadeInCard>

        {/* Recent Reviews */}
        <FadeInCard delay={340} style={{ padding: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Recent Reviews
          </h3>
          {analytics.recent_reviews && analytics.recent_reviews.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {analytics.recent_reviews.map((review) => (
                <div key={review.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  background: 'var(--bg)',
                  borderRadius: 8,
                  transition: 'background 0.15s',
                }}>
                  <span style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: RATING_COLORS[review.rating],
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0,
                  }}>
                    {review.rating}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                      {review.reviewer_name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-light)' }}>
                      {formatRelative(review.date)}
                    </div>
                  </div>
                  <span className={`review-status ${cleanStatus(review.status)}`}>
                    {cleanStatus(review.status)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state" style={{ padding: 24 }}>
              <div className="empty-state-text">No reviews yet</div>
            </div>
          )}
        </FadeInCard>
      </div>

      {/* Rating Distribution + Average Rating Trend — side by side */}
      <div className="dashboard-stats-detail">
        {/* Rating Distribution */}
        <FadeInCard delay={400} style={{ padding: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Rating Distribution
          </h3>
          <p style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 0, marginBottom: 16 }}>
            Breakdown of all ratings received
          </p>
          {rating_distribution && Object.keys(rating_distribution).length > 0 ? (
            <RatingDistribution
              distribution={rating_distribution}
              total={response_metrics.total}
            />
          ) : response_metrics.total > 0 && analytics.individual_reviews?.length > 0 ? (
            /* Compute from individual reviews if API didn't provide distribution */
            <RatingDistribution
              distribution={analytics.individual_reviews.reduce((acc, r) => {
                acc[r.rating] = (acc[r.rating] || 0) + 1;
                return acc;
              }, {})}
              total={analytics.individual_reviews.length}
            />
          ) : (
            <div className="empty-state" style={{ padding: 24 }}>
              <div className="empty-state-text">No ratings yet</div>
            </div>
          )}
        </FadeInCard>

        {/* Average Rating Trend */}
        <FadeInCard delay={460} style={{ padding: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Average Rating Trend
          </h3>
          <p style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 0, marginBottom: 16 }}>
            How your rating changes over time
          </p>
          {reviews_over_time.length > 1 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={reviews_over_time} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="period_label" tick={{ fontSize: 11, fill: 'var(--text-light)' }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 11, fill: 'var(--text-light)' }} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                  formatter={(value) => [value, 'Avg Rating']}
                />
                <Line
                  type="monotone"
                  dataKey="avg_rating"
                  stroke="#34d399"
                  strokeWidth={3}
                  dot={{ fill: '#34d399', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state" style={{ padding: 24 }}>
              <div className="empty-state-text">
                {rating_trend?.current_avg
                  ? `Current: ${rating_trend.current_avg} ★ — trend appears with more data`
                  : 'No data yet'}
              </div>
            </div>
          )}
        </FadeInCard>
      </div>

      {/* Review Request Link — styled as CTA banner */}
      <FadeInCard delay={520} className="review-link-banner" style={{ padding: 24, marginBottom: 24 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Review Request Link
        </h3>
        <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 16 }}>
          Share this link with customers to collect Google reviews. Put it on receipts, emails, or table cards.
        </p>
        {reviewLink ? (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 16px',
              background: 'var(--bg)',
              borderRadius: 8,
              border: '1px solid var(--border)',
              marginBottom: 12,
            }}>
              <span style={{ flex: 1, fontSize: 14, fontWeight: 500, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {reviewLink.full_link}
              </span>
              <button className="btn btn-primary" onClick={copyLink} style={{ flexShrink: 0 }}>
                {linkCopied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-light)' }}>
              {reviewLink.clicks} click{reviewLink.clicks !== 1 ? 's' : ''} so far
            </span>
          </div>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 0 }}>
            Set up your review link in{' '}
            <a href="/settings" style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>
              Settings
            </a>{' '}
            to start collecting reviews.
          </p>
        )}
      </FadeInCard>

      <CompetitorInsights />

      {/* Source Breakdown */}
      {source_breakdown.length > 0 && (
        <FadeInCard delay={580} style={{ padding: 24, marginTop: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Review Sources
          </h3>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {source_breakdown.map(s => (
              <div key={s.source} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={`source-badge ${s.source}`} style={{ fontSize: 12 }}>
                  {s.source}
                </span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{s.count}</span>
              </div>
            ))}
          </div>
        </FadeInCard>
      )}
    </div>
  );
}
